import type React from "react";
import {
  Group,
  NumberInput,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconChecks } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface BulkAcceptPopoverProps {
  opened: boolean;
  onOpenedChange: (opened: boolean) => void;
  disabled: boolean;
  running: boolean;
  threshold: number;
  onThresholdChange: (value: number) => void;
  eligibleCount: number;
  summary: string | null;
  onCancel: () => void;
  onApply: () => void;
}

const BulkAcceptPopover: React.FC<BulkAcceptPopoverProps> = ({
  opened,
  onOpenedChange,
  disabled,
  running,
  threshold,
  onThresholdChange,
  eligibleCount,
  summary,
  onCancel,
  onApply,
}) => {
  const { t } = useTranslation();
  return (
    <Popover
      position="bottom-end"
      opened={opened}
      onChange={onOpenedChange}
      withArrow
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <Button
          size="sm"
          variant="light"
          color="green"
          leftSection={<IconChecks size={16} />}
          onClick={() => onOpenedChange(!opened)}
          disabled={disabled}
        >
          {t("jobResultsPage.bulk.cta")}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs" miw={260}>
          <Text size="sm" fw={500}>
            {t("jobResultsPage.bulk.title")}
          </Text>
          <Text size="xs" c="dimmed">
            {t("jobResultsPage.bulk.description")}
          </Text>
          <NumberInput
            size="xs"
            label={t("jobResultsPage.bulk.thresholdLabel")}
            value={threshold}
            onChange={(value) =>
              onThresholdChange(typeof value === "number" ? value : 90)
            }
            min={50}
            max={100}
            step={1}
            suffix="%"
          />
          <Text size="xs" c="dimmed">
            {t("jobResultsPage.bulk.eligibleCount", {
              count: eligibleCount,
              defaultValue: `${eligibleCount} pending rows match`,
            })}
          </Text>
          {summary && (
            <Text size="xs" c="teal">
              {summary}
            </Text>
          )}
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="subtle"
              onClick={onCancel}
              disabled={running}
            >
              {t("jobResultsPage.bulk.cancel")}
            </Button>
            <Button
              size="xs"
              loading={running}
              disabled={eligibleCount === 0}
              onClick={onApply}
            >
              {t("jobResultsPage.bulk.apply")}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default BulkAcceptPopover;
